import {i18n} from '@online-offer/i18n';
import {SectionPrint} from '@online-offer/shared';
import {Grid, GridItem, HStack, Show, ThemeIconKey} from 'platform/foundation';
import {formatNumber, useLocale} from 'platform/locale';

import {any, isNotNil, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {Badge} from '../../components/Badge/Badge';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {SpecificationItem} from './components/SpecificationItem';
import {getBadgeLetter} from './utils/getBadgeLetter';
import {getSpecification} from './utils/getSpecification';

const KwToHPMultiplier = 1.35962;

const convertKwToHP = (kw: number): number => Math.round(kw * KwToHPMultiplier);

const formatPowerWithHorsepower = (kw: number): string => {
  const hp = convertKwToHP(kw);
  return `${kw} ${i18n.t('metricKw')} / ${hp} ${i18n.t('metricHp')}`;
};

function GridSpecificationItem({
  icon,
  categoryTitle,
  value,
}: {
  value: string | Nullish;
  categoryTitle: string;
  icon: ThemeIconKey;
}) {
  if (isNilOrEmpty(value)) {
    return null;
  }
  return (
    <GridItem>
      <SpecificationItem value={value!} categoryTitle={categoryTitle} icon={icon} />
    </GridItem>
  );
}

export function SpecificationPrint() {
  const {vehicle, alphaCatalogue} = useGetOnlineOfferData();
  const locale = useLocale();

  if (!vehicle) {
    return null;
  }

  const specificationData = getSpecification(vehicle, alphaCatalogue);

  if (!specificationData || !any(isNotNilOrEmpty, values(specificationData))) {
    return null;
  }

  const {
    bodywork,
    mileage,
    power,
    differential,
    transmission,
    fuel,
    co2Emissions,
    yearOfManufacture,
  } = specificationData;

  const mileageValue = isNotNil(mileage)
    ? `${formatNumber(locale.localeConfig, mileage, 0)} ${i18n.t('metricKm')}`
    : null;

  const powerValue = isNotNil(power) ? formatPowerWithHorsepower(power) : null;

  const badgeLetter = getBadgeLetter(co2Emissions);

  const co2EmissionsValue = isNotNil(co2Emissions)
    ? `${co2Emissions} ${i18n.t('metricGkm')}`
    : null;

  const yearOfManufactureValue = yearOfManufacture?.split('-')?.[0];

  return (
    <SectionPrint heading={i18n.t('specification.heading')}>
      <Grid columns={2} verticalSpacing={8}>
        <GridSpecificationItem
          value={yearOfManufactureValue}
          categoryTitle={i18n.t('specification.item.year')}
          icon="specification/year"
        />
        <GridSpecificationItem
          value={mileageValue}
          categoryTitle={i18n.t('specification.item.mileage')}
          icon="specification/mileage"
        />
        <GridSpecificationItem
          value={bodywork}
          categoryTitle={i18n.t('specification.item.vehicle')}
          icon="specification/vehicle"
        />
        <GridSpecificationItem
          value={differential}
          categoryTitle={i18n.t('specification.item.driveType')}
          icon="specification/drive_type"
        />
        <GridSpecificationItem
          value={transmission}
          categoryTitle={i18n.t('specification.item.transmission')}
          icon="specification/transmission"
        />
        <GridSpecificationItem
          value={fuel}
          categoryTitle={i18n.t('specification.item.fuelType')}
          icon="specification/fuel_type"
        />
        <GridSpecificationItem
          value={powerValue}
          categoryTitle={i18n.t('specification.item.power')}
          icon="specification/power"
        />
        <Show when={isNotNilOrEmpty(co2EmissionsValue)}>
          <GridItem>
            <HStack align="flex-end" spacing={1}>
              <SpecificationItem
                value={co2EmissionsValue!}
                categoryTitle={i18n.t('specification.item.co2')}
                icon="specification/co2"
              />
              <Show when={isNotNil(badgeLetter)}>
                <Badge variant={badgeLetter!} />
              </Show>
            </HStack>
          </GridItem>
        </Show>
      </Grid>
    </SectionPrint>
  );
}
